"use client";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ChevronDownIcon } from "lucide-react";
import type { ComponentProps } from "react";
import { createContext, memo, useContext } from "react";
import { Shimmer } from "./shimmer";

type PlanContextValue = {
  isStreaming: boolean;
};

const PlanContext = createContext<PlanContextValue>({ isStreaming: false });

function usePlan(): PlanContextValue {
  return useContext(PlanContext);
}

export type PlanProps = ComponentProps<typeof Collapsible> & {
  isStreaming?: boolean;
};

export const Plan = memo(
  ({ isStreaming = false, className, children, ...props }: PlanProps) => (
    <PlanContext.Provider value={{ isStreaming }}>
      <Collapsible
        data-slot="plan"
        className={cn(
          "rounded-xl border bg-card text-card-foreground shadow-sm",
          className
        )}
        {...props}
      >
        {children}
      </Collapsible>
    </PlanContext.Provider>
  )
);

export type PlanHeaderProps = ComponentProps<"div">;

export const PlanHeader = memo(
  ({ className, ...props }: PlanHeaderProps) => (
    <div
      data-slot="plan-header"
      className={cn(
        "flex items-start justify-between gap-4 p-6",
        className
      )}
      {...props}
    />
  )
);

export type PlanTitleProps = ComponentProps<"div">;

export const PlanTitle = memo(
  ({ children, className, ...props }: PlanTitleProps) => {
    const { isStreaming } = usePlan();
    return (
      <div
        data-slot="plan-title"
        className={cn("font-semibold leading-none tracking-tight", className)}
        {...props}
      >
        {isStreaming && typeof children === "string" ? (
          <Shimmer>{children}</Shimmer>
        ) : (
          children
        )}
      </div>
    );
  }
);

export type PlanDescriptionProps = ComponentProps<"div">;

export const PlanDescription = memo(
  ({ children, className, ...props }: PlanDescriptionProps) => {
    const { isStreaming } = usePlan();
    return (
      <div
        data-slot="plan-description"
        className={cn("text-sm text-muted-foreground mt-1.5", className)}
        {...props}
      >
        {isStreaming && typeof children === "string" ? (
          <Shimmer>{children}</Shimmer>
        ) : (
          children
        )}
      </div>
    );
  }
);

export type PlanTriggerProps = ComponentProps<typeof CollapsibleTrigger>;

export const PlanTrigger = memo(
  ({ className, children, ...props }: PlanTriggerProps) => (
    <CollapsibleTrigger
      asChild
      data-slot="plan-trigger"
      className={cn("group", className)}
      {...props}
    >
      {children ?? (
        <Button variant="ghost" size="icon" className="shrink-0">
          <ChevronDownIcon className="size-4 transition-transform group-data-[state=open]:rotate-180" />
        </Button>
      )}
    </CollapsibleTrigger>
  )
);

export type PlanContentProps = ComponentProps<typeof CollapsibleContent>;

export const PlanContent = memo(
  ({ className, children, ...props }: PlanContentProps) => (
    <CollapsibleContent data-slot="plan-content" {...props}>
      <div
        className={cn("px-6 pb-4 text-sm text-muted-foreground", className)}
      >
        {children}
      </div>
    </CollapsibleContent>
  )
);

export type PlanFooterProps = ComponentProps<"div">;

export const PlanFooter = memo(
  ({ className, ...props }: PlanFooterProps) => (
    <div
      data-slot="plan-footer"
      className={cn(
        "flex items-center justify-end gap-2 px-6 py-4 border-t",
        className
      )}
      {...props}
    />
  )
);

export type PlanActionProps = ComponentProps<typeof Button>;

export const PlanAction = memo(
  ({ className, ...props }: PlanActionProps) => (
    <Button data-slot="plan-action" className={className} {...props} />
  )
);

Plan.displayName = "Plan";
PlanHeader.displayName = "PlanHeader";
PlanTitle.displayName = "PlanTitle";
PlanDescription.displayName = "PlanDescription";
PlanTrigger.displayName = "PlanTrigger";
PlanContent.displayName = "PlanContent";
PlanFooter.displayName = "PlanFooter";
PlanAction.displayName = "PlanAction";
